// React.
import { useEffect, useMemo, useState } from "react";

// Third-parties.
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import { CSVLink } from 'react-csv';

// Types.
import { ParameterComparisonTableProps } from "./types";
import { ParameterComparison } from '../../../types/instance/Parameter';

// Components.
import NoData from '../../../component/NoData';
import TablePagination from '../../../component/TablePagination';

// Helpers.
import * as dayjs from 'dayjs';
import { archiverUrl, fetchResults, fetchWithAuthorization, getDefaultPageSize, getTimeZone } from "../../../helpers/utils";
import { showError } from '../../../utils/showAlert';
import Helper from "../../../helpers/Helper";
import SearchInput from '../../../component/SearchInput';

const ParameterComparisonTable = (props: ParameterComparisonTableProps) => {
    const {instanceOptions, generateId} = props;

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [totalDifferences, setTotalDifferences] = useState<number>(0);
    const [data, setData] = useState<ParameterComparison[]>([]);
    const [tableFilter, setTableFilter] = useState<string>('');

    const tz = getTimeZone();

    useEffect(() => {
        async function load() {
            try {
                setLoading(true);

                const iso = 'YYYY-MM-DD+HH:mm';
                const from0 = dayjs.default(instanceOptions[0].periodStart)
                    .format(iso);
                const to0 = dayjs.default(instanceOptions[0].periodEnd)
                    .format(iso);
                const from1 = dayjs.default(instanceOptions[1].periodStart)
                    .format(iso);
                const to1 = dayjs.default(instanceOptions[1].periodEnd)
                    .format(iso);
                const id0 = instanceOptions[0].instanceId;
                const id1 = instanceOptions[1].instanceId;

                const promises: Promise<any>[] =
                    [
                        fetchWithAuthorization(archiverUrl() +
                            '/datasource/parameter/comparison?tz=' + tz +
                            '&from=' + from0 + '&to=' + to0 + '&id=' + id0 +
                            '&from=' + from1 + '&to=' + to1 + '&id=' + id1)
                    ];

                const result: any[][] = await fetchResults(promises);

                if (active) {
                    const dataWithDiff = result[0].map((row) => ({
                        ...row,
                        isDifferent: row.value1 !== row.value2, // Add the isDifferent property
                    }))

                    const totalDifferences = dataWithDiff.filter((row) => row.isDifferent).length;

                    setData(dataWithDiff);
                    setTotalDifferences(totalDifferences);
                    setLoading(false);
                }
            } catch (x: any) {
                console.log(x.message);
                const errorMessage = x.message || x.toString();
                setError(errorMessage);
                showError('Failed to load parameter comparison', errorMessage);
                setData([]);
                setLoading(false);
            }
        }

        let active: boolean = true;
        load();
        return function () {
            active = false;
        }
    }, [generateId]);

    // CSV headers.
    const headers: any[] = useMemo(() => {
        return [
            {
                label: 'Parameter',
                key: 'parameter'
            },
            {
                label: `${props.instanceOptions[0].instanceName} (${Helper.getFormattedPeriodHeadingFromDate(
                        props.instanceOptions[0].periodStart,
                        props.instanceOptions[0].periodEnd
                )})`,
                key: 'value1'
            },
            {
                label: `${props.instanceOptions[1].instanceName} (${Helper.getFormattedPeriodHeadingFromDate(
                        props.instanceOptions[1].periodStart,
                        props.instanceOptions[1].periodEnd
                )})`,
                key: 'value2'
            },
            {
                label: 'Is Different',
                key: 'isDifferent'
            }
        ];
    }, [props.instanceOptions]);


    const columns: Column[] = useMemo(() => {
        return [
            {
                Header: 'Parameter',
                accessor: 'parameter',
                headerClassName: 'text',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: (
                        <div className="text-center d-inline-block">
                            {props.instanceOptions[0].instanceName}
                            <br />
                            ({Helper.getFormattedPeriodHeadingFromDate(props.instanceOptions[0].periodStart, props.instanceOptions[0].periodEnd)})
                        </div>
                ),
                accessor: 'value1',
                className: 'text-center',
                headerClassName: 'text text-center',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: (
                        <div className="text-center d-inline-block">
                            {props.instanceOptions[1].instanceName}
                            <br />
                            ({Helper.getFormattedPeriodHeadingFromDate(props.instanceOptions[1].periodStart, props.instanceOptions[1].periodEnd)})
                        </div>
                ),
                accessor: 'value2',
                className: 'text-center',
                headerClassName: 'text text-center',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Is Different',
                accessor: 'isDifferent',
                className: 'text-center',
                headerClassName: 'text text-center',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic',
                Cell: ({ value }) => (value ? 'Yes' : 'No')
            }
        ]
    }, [data]);

    const tableData = useMemo(() =>
            data.filter((row) =>
                row.parameter.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.value1?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                row.value2?.toLowerCase().includes(tableFilter.toLowerCase())),
        [data, tableFilter]);

    const csvData = useMemo(() => {
        // Escape embedded double-quotation marks (") in the values.
        const csvData: any[] = JSON.parse(JSON.stringify(data));

        csvData.forEach((item) => {
            if (item.value1) {
                item.value1 = item.value1.replaceAll('"', '""');
            }

            if (item.value2) {
                item.value2 = item.value2.replaceAll('"', '""');
            }

            // Convert isDifferent boolean to 'Yes' or 'No' for CSV export.
            if (item.isDifferent !== undefined) {
                item.isDifferent = item.isDifferent ? 'Yes' : 'No';
            }
        });

        return csvData;
    }, [data]);

    const
        {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            prepareRow,
            page,
            setPageSize,
            pageOptions,
            pageCount,
            canPreviousPage,
            previousPage,
            canNextPage,
            nextPage,
            gotoPage,
            state: {pageIndex, pageSize}
        } = useTable(
            {
                columns,
                data: tableData,
                initialState:
                    {
                        pageIndex: 0,
                        pageSize: getDefaultPageSize()
                    },
                disableSortRemove: true
            }, useSortBy, usePagination);

    function clearTableFilter() {
        setTableFilter('');
    }

    return <>
        <div className="card">
            <div className="card-header">
                <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
                Parameters
                <span className="badge bg-info"
                      data-tip="Total Parameters">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapse-usage" aria-expanded="false" aria-controls="collapse-usage"/> <span className='warning-message'>({totalDifferences} {totalDifferences === 1 ? 'difference' : 'differences'})</span>
            </div>
            <div id="collapse-usage" className="card-body collapse show">
                <div className="row row-cols-1 row-cols-md-2 table-search">
                    <div className="col col-md-10">
                        <CSVLink role="button"
                                 headers={headers}
                                 data={csvData}
                                 download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - Parameters.csv`}
                                 className="btn btn-sm btn-primary">
                            <i className="fal fa-file-export fa-fw"/>
                            <span>Export</span>
                        </CSVLink>
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                            onClear={clearTableFilter}
                            autoFocus
                        />
                    </div>
                </div>
                <div className="table-responsive">
                    <table {...getTableProps()} className="table">
                        <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                        className={(column as any).headerClassName}>
                                        {column.render('Header')}
                                        {column.canSort ? (column.isSorted ? (column.isSortedDesc ?
                                                <i className="fal fa-sort-amount-up-alt"></i> :
                                                <i className="fal fa-sort-amount-down-alt"></i>) :
                                            <i className="fal fa-sort-amount-down-alt text-light"></i>) : ''}
                                    </th>))}
                            </tr>))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                            prepareRow(row);
                            return (
                                // @ts-ignore
                                <tr {...row.getRowProps()} className={row.original.isDifferent ? 'row-different' : ''}>
                                    {row.cells.map((cell) => {
                                        return (
                                            <td {...cell.getCellProps()}
                                                className={(cell.column as any).className}>
                                                {cell.render('Cell')}
                                            </td>)
                                    })}
                                </tr>)
                        })}
                        </tbody>
                    </table>
                </div>
                <NoData error={error} loading={loading}
                        length={data.length}/>
                <TablePagination
                    length={data.length}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    canPreviousPage={canPreviousPage}
                    previousPage={previousPage}
                    canNextPage={canNextPage}
                    nextPage={nextPage}
                    gotoPage={gotoPage}
                    pageIndex={pageIndex}
                />
            </div>
        </div>
    </>
}

export default ParameterComparisonTable;
